// The Data Streams list and its satellite views: edit, process, history, details.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { T as O } from '../../../shared/styles/tokens.js';
import { reachApi, RUN_STATUS } from '../api.js';
import { formatDuration, formatRunTimestamp, nextRunLabel, ordinal, shortStreamId } from '../format.js';
import { BackButton, CADENCES_SCHEDULE, ColumnHeaderFilter, DateRangePicker, Field, LoadingState, Modal, PLATFORMS, ResizableHeaderCell, RunStatusBadge, StatusToggle, T, TimeSelect, ToolbarButton } from '../components/ui.jsx';
import { labelForKpi, labelForLevel, liveAccountLabel, useAdAccounts, useConnections } from '../hooks.js';
import { ExtractionPicker, FieldDropdown, TimezoneSelect } from '../components/pickers.jsx';
import { PivotTableView } from './PivotPage.jsx';
import { useUnsavedChanges } from '../unsavedChanges.js';
import { validLookbackDays } from '../streamSetup.js';

export function StreamEditFields({ draft, setDraft, onFieldsUnavailable }) {
  const { loading: loadingConnections, connections, error: connectionError } =
    useConnections(draft.platform);
  const { loading: loadingAdAccounts, accounts: liveAdAccounts, error: adAccountError } =
    useAdAccounts(draft.connectionId);

  // The stream's CURRENT ad account is always an option, even when the live list no longer
  // contains it — a token that lost access to an account must not silently blank the field and
  // turn a save into a deletion of the very thing the user came here to edit.
  const adAccountOptions = useMemo(() => {
    const byId = new Map(liveAdAccounts.map(account => [account.external_id, account]));
    for (const id of draft.adAccounts || []) {
      if (!byId.has(id)) byId.set(id, { external_id: id, name: '' });
    }
    return [...byId.values()];
  }, [liveAdAccounts, draft.adAccounts]);

  const selectStyle = (enabled) => ({
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8,
    border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 13, fontFamily: 'inherit',
    cursor: enabled ? 'pointer' : 'not-allowed', opacity: enabled ? 1 : 0.5,
  });

  // Changing the connection clears the ad account: the new email reaches a different set, and
  // silently keeping an id the new token cannot see would produce a stream that saves fine and
  // fails at the API with a permissions error nobody can trace back to this moment.
  const handleConnectionChange = (connectionId) => {
    setDraft({ ...draft, connectionId, adAccounts: [] });
  };

  return (
    <div className="reach-form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      <Field label="Connection">
        <select
          value={draft.connectionId || ''}
          onChange={e => handleConnectionChange(e.target.value)}
          disabled={loadingConnections || !!connectionError}
          style={selectStyle(!loadingConnections && !connectionError)}
        >
          <option value="" disabled>{loadingConnections ? 'Loading connections' : 'Select a connected email'}</option>
          {/* The stream's current connection stays listed even when it is expired or the list
              could not be read, so opening this form never blanks a field the user did not
              touch. */}
          {draft.connectionId && !connections.some(c => c.id === draft.connectionId)
            && <option value={draft.connectionId}>{draft.connectionLabel || 'Current connection'}</option>}
          {connections.map(connection => <option key={connection.id} value={connection.id}>{connection.label}</option>)}
        </select>
        {connectionError && <p className="o-note" style={{ margin: '8px 0 0' }}>{connectionError}</p>}
      </Field>
      <Field label="Ad account">
        <select
          value={draft.adAccounts?.[0] || ''}
          onChange={e => setDraft({ ...draft, adAccounts: e.target.value ? [e.target.value] : [] })}
          disabled={loadingAdAccounts || adAccountOptions.length === 0}
          style={selectStyle(!loadingAdAccounts && adAccountOptions.length > 0)}
        >
          <option value="" disabled>{loadingAdAccounts ? 'Reading ad accounts' : 'Select ad account'}</option>
          {adAccountOptions.map(account => (
            <option key={account.external_id} value={account.external_id}>{liveAccountLabel(account)}</option>
          ))}
        </select>
        {/* Shown under the control rather than replacing it, because the stream's existing ad
            account is still selectable and still valid while the connection is being fixed. */}
        {adAccountError && !loadingAdAccounts && <p className="o-note" style={{ margin: '8px 0 0' }}>{adAccountError}</p>}
      </Field>
      <Field label="Run frequency">
        <select className="o-control reach-native-select" aria-label="Run frequency" value={draft.cadence || ''}
          onChange={event => setDraft({ ...draft, cadence: event.target.value })}>
          <option value="" disabled>Select run frequency</option>
          {CADENCES_SCHEDULE.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
        </select>
      </Field>
      <Field label="Time">
        <TimeSelect
          value={draft.time}
          onChange={v => setDraft({ ...draft, time: v })}
          style={{ padding: '8px 10px', borderRadius: 8, background: T.bg }}
        />
      </Field>
      <Field label="Timezone"><TimezoneSelect value={draft.timezone || 'Asia/Beirut'} onChange={timezone => setDraft({ ...draft, timezone })} /></Field>
      {draft.cadence === 'weekly' && <Field label="Run on"><FieldDropdown label="Run days" compact chips options={['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(id => ({ id, label: id }))} selected={draft.weekdays || []} onChange={weekdays => setDraft({ ...draft, weekdays })} /></Field>}
      {draft.cadence === 'monthly' && <Field label="Day of month"><select className="o-control" value={draft.monthDay || 1} onChange={event => setDraft({ ...draft, monthDay: Number(event.target.value) })}>{Array.from({ length: 28 }, (_, index) => <option key={index + 1} value={index + 1}>{ordinal(index + 1)}</option>)}</select></Field>}
      <Field label="Lookback window (days)"><input className="o-control" aria-label="Lookback window (days)" type="number" min="1" max="365" step="1"
        disabled={draft.loadMode === 'total_replace'} value={draft.lookbackDays ?? ''} placeholder="Reporting-period default"
        onChange={event => setDraft({ ...draft, lookbackDays: event.target.value === '' ? null : Number(event.target.value) })} />
        <p className="o-note">{draft.loadMode === 'total_replace' ? 'Total Replace refreshes all initial history.' : 'Refresh the previous 1–365 days through yesterday. Leave blank to retain the reporting-period default. Affected periods keep their full, de-duplicated reach.'}</p>
      </Field>
      {/* Levels and metrics are editable after creation, or the only way to add a metric
          would be deleting the stream and rebuilding it. Full width: two chipsets crammed
          into half a column wrap into an unreadable ladder. */}
      <div style={{ gridColumn: '1 / -1' }}>
        <ExtractionPicker
          platform={draft.platform}
          levels={draft.levels || []}
          kpis={draft.kpis || []}
          basic
          onLoadingChange={onFieldsUnavailable}
          onChange={selection => setDraft({ ...draft, fieldProfile: 'basic', levels: selection.levels, kpis: selection.kpis })}
        />
      </div>
    </div>
  );
}


export function EditStreamView({ stream, onSave, onCancel }) {
  const [fieldsUnavailable, setFieldsUnavailable] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const save = async () => {
    if (saving) return;
    setSaving(true); setSaveError('');
    try { await onSave(draft); }
    catch (error) { setSaveError(error.message || 'The stream could not be saved. Your changes are still here.'); }
    finally { setSaving(false); }
  };
  // Normalize legacy accounts (from before the email->account dropdown existed)
  // so it starts empty instead of showing a value that isn't in any email's list.
  const [draft, setDraft] = useState(() => stream.email
    ? stream
    : { ...stream, accounts: [] });
  useUnsavedChanges(JSON.stringify(draft) !== JSON.stringify(stream.email ? stream : { ...stream, accounts: [] }), JSON.stringify(draft));
  return (
    <fieldset disabled={saving} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: T.text }}>Edit stream</h2>
      <Field label="Stream name">
        <input
          type="text"
          value={draft.name}
          onChange={e => setDraft({ ...draft, name: e.target.value })}
          style={{
            width: '100%', maxWidth: 420, boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8,
            border: `1px solid ${T.border}`, background: T.card, color: T.text, fontSize: 13, fontFamily: 'inherit',
          }}
        />
      </Field>
      <StreamEditFields draft={draft} setDraft={setDraft} onFieldsUnavailable={setFieldsUnavailable} />
      <div className="reach-form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}><Field label="Owner"><input className="o-control" type="email" value={draft.owner || draft.email || ''} onChange={event => setDraft({ ...draft, owner: event.target.value })} /></Field><Field label="Tags"><input className="o-control" value={(draft.tags || []).join(', ')} onChange={event => setDraft({ ...draft, tags: event.target.value.split(',').map(tag => tag.trim()).filter(Boolean) })} placeholder="paid-social, weekly" /></Field></div>
      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button type="button" className="o-btn" data-reach-leave disabled={saving} onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="o-btn o-btn-primary" disabled={saving || fieldsUnavailable || !draft.name.trim() || !draft.connectionId || !draft.adAccounts?.length || (draft.cadence === 'weekly' && !draft.weekdays?.length) || (draft.loadMode !== 'total_replace' && draft.lookbackDays != null && !validLookbackDays(draft.lookbackDays))} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      {saveError && <div className="o-hint o-error" role="alert">{saveError}</div>}
    </fieldset>
  );
}


export function ProcessView({ streams, onRun }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');
  const [ongoingDate, setOngoingDate] = useState(false);
  const [busy, setBusy]           = useState(false);
  const [outcome, setOutcome]     = useState(null);
  const [error, setError]         = useState('');
  const [queuedStreams, setQueuedStreams] = useState({});
  // "Ongoing" means no end date, so a range is valid without one.
  const validRange = Boolean(startDate && (ongoingDate || (endDate && startDate <= endDate)));

  const trigger = async () => {
    if (busy || !validRange) return;
    setBusy(true);
    setError('');
    if (!Object.keys(queuedStreams).length) setOutcome(null);
    try {
      // Sequential so a failure on the third stream still leaves the first two queued, and so
      // the error names the stream that failed rather than an arbitrary one from a race.
      const queued = { ...queuedStreams };
      for (const stream of streams) {
        if (queued[stream.id]) continue;
        queued[stream.id] = await onRun(stream.id, {
          from: startDate,
          to: ongoingDate ? null : endDate,
          ongoing: ongoingDate,
        });
        setQueuedStreams({ ...queued });
        setOutcome({ count: Object.keys(queued).length, jobs: Object.values(queued).reduce((sum, job) => sum + (Number(job.jobs) || 1), 0),
          startedNow: Object.values(queued).every(job => job.started_now) });
      }
      setOutcome({
        count: Object.keys(queued).length,
        jobs: Object.values(queued).reduce((sum, job) => sum + (Number(job.jobs) || 1), 0),
        // started_now false is not a failure: the job is durably queued and the batch layer
        // sweeps every 30 seconds. It only changes how soon, so it only changes the wording.
        startedNow: Object.values(queued).every(job => job.started_now),
      });
    } catch (requestError) {
      setError(requestError?.message || 'Could not queue the reprocess.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: T.text }}>Force trigger / reprocess</h2>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: T.muted }}>
        Reprocess the selected stream{streams.length > 1 ? 's' : ''} for a specific date range.
      </p>

      <Field label={`Streams (${streams.length})`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {streams.map(s => (
            <span key={s.id} style={{ fontSize: 13, color: T.text }}>• {s.name}</span>
          ))}
        </div>
      </Field>

      <Field label="Date range"><DateRangePicker disabled={busy} modal label="Reprocess date range" value={{ from: startDate, to: endDate, ongoing: ongoingDate }} onChange={({ from, to, ongoing = false }) => { setStartDate(from); setEndDate(to); setOngoingDate(ongoing); setOutcome(null); setQueuedStreams({}); setError(''); }} /></Field>

      {startDate && endDate && !validRange && <div className="o-hint" style={{ marginBottom: 12 }}><b>Invalid date range.</b> End date must be on or after start date.</div>}

      <button
        type="button"
        className="o-btn o-btn-primary"
        disabled={!validRange || busy || streams.length === 0 || streams.every(stream => queuedStreams[stream.id])}
        onClick={trigger}
      >
        {busy ? 'Queueing…' : streams.length > 0 && streams.every(stream => queuedStreams[stream.id]) ? 'Reprocess queued' : Object.keys(queuedStreams).length ? 'Retry remaining streams' : 'Trigger reprocess'}
      </button>

      {outcome && (
        <div className="o-hint" style={{ marginTop: 12 }}>
          <b>Queued {outcome.jobs} {outcome.jobs === 1 ? 'job' : 'jobs'} for {outcome.count} {outcome.count === 1 ? 'stream' : 'streams'}</b> for {startDate} → {ongoingDate ? 'today' : endDate}.{' '}
          {outcome.startedNow
            ? 'Extraction has started — the run status updates as it progresses.'
            : 'The jobs are waiting for the extraction worker.'}
          {' '}Each stream uses its configured load mode. Incremental keeps existing rows; Upsert updates matching rows; Replace refreshes the selected periods; Total Replace refreshes all initial history.
        </div>
      )}

      {error && (
        <div className="o-state o-error" style={{ marginTop: 12 }}>
          <b>Could not queue the reprocess.</b> {error}
        </div>
      )}
    </div>
  );
}


// Real run history for one stream, from bp_queue joined to bp_run_log.
//
// This screen used to invent its rows — five fabricated runs with Math.random() records and
// durations, regenerated on every render. It sat next to real extraction data with nothing
// distinguishing the two, which is the kind of thing someone eventually makes a decision on.
// A stream that has never run now says so rather than showing five plausible successes.
export function ExecutionHistoryView({ stream, onRetry }) {
  const [rows, setRows] = useState(null);      // null = loading; [] = loaded and empty
  const [error, setError] = useState('');
  const [selectedFailure, setSelectedFailure] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    try {
      setError('');
      setRows(await reachApi.history(stream.id));
    } catch (err) {
      if (!silent) setRows([]);
      setError(err?.message || 'Run history could not be loaded.');
    }
  }, [stream.id]);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => {
      if (!document.hidden) load({ silent: true });
    }, 3000);
    return () => window.clearInterval(timer);
  }, [load]);

  const retrySelectedFailure = async () => {
    const failure = selectedFailure;
    setSelectedFailure(null);
    try {
      await onRetry(failure);
      await load();
    } catch (retryError) {
      setError(retryError?.message || 'The run could not be retried.');
    }
  };

  return (
    <div>
      <div className="o-head-row">
        <div>
          <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Execution history — {stream.name}</h2>
          <p className="o-note" style={{ margin: 0 }}>Every queued run for this stream, newest first.</p>
        </div>
        <button type="button" className="o-btn o-btn-sm" onClick={() => load()}>Refresh</button>
      </div>

      {error && <div className="o-state o-error" style={{ marginBottom: 16 }}>{error}</div>}

      {rows === null
        ? <LoadingState title="Loading execution history…" detail="Reading the latest run attempts and their current status." style={{ maxWidth: 'none', border: 0 }} />
        : rows.length === 0
          ? <div className="o-hint"><b>This stream has never run.</b> Use Process to queue an extraction, or wait for its next scheduled run.</div>
          : <div className="o-panel" style={{ overflowX: 'auto' }}>
              <table className="o-tbl" style={{ width: '100%', minWidth: 820 }}>
                <thead>
                  <tr>
                    <th>Run date</th>
                    <th>Data range</th>
                    <th>Status</th>
                    <th>Rows</th>
                    <th>Duration</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(run => {
                    const status = RUN_STATUS[run.status] || run.status;
                    return (
                      <tr key={run.queue_id}>
                        {/* When a run finished, that is the meaningful moment; while it is still
                            queued or running there is no finish yet, so fall back to when it was
                            asked for rather than showing an empty cell. */}
                        <td className="o-num">{formatRunTimestamp(run.finished_at || run.created_at)}</td>
                        <td className="o-num">{run.date_from} → {run.date_to}</td>
                        <td>
                          <RunStatusBadge status={status} />
                          {/* Progress is only meaningful mid-flight. A finished run showing
                              "3/3" is noise; a running one showing nothing is a mystery. */}
                          {run.status === 'running' && run.progress_total > 0
                            && <span className="o-note" style={{ marginLeft: 8 }}>{run.progress_done}/{run.progress_total}</span>}
                        </td>
                        {/* Rows actually written, summed from the run log's successful chunks —
                            not a count of anything fetched. A failed run genuinely wrote zero. */}
                        <td className="o-num">{Number(run.rows_written || 0).toLocaleString()}</td>
                        <td className="o-num">{formatDuration(run.claimed_at, run.finished_at)}</td>
                        <td className="o-acts">
                          {run.last_error
                            ? <button type="button" className="o-btn o-btn-sm" onClick={() => setSelectedFailure(run)}>View failure</button>
                            : <span className="o-note">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>}

      {selectedFailure && <div className="o-scrim" role="dialog" aria-modal="true" onClick={() => setSelectedFailure(null)}>
        <form className="o-modal" onClick={event => event.stopPropagation()} onSubmit={event => event.preventDefault()}>
          <div className="o-modal-head"><h2 style={{ margin: 0, fontSize: 18 }}>Run failure</h2></div>
          <div className="o-modal-body" style={{ display: 'grid', gap: 16 }}>
            <div>
              <span className="o-field-lbl">Data range</span>
              <p className="o-num" style={{ margin: '4px 0 0' }}>{selectedFailure.date_from} → {selectedFailure.date_to}</p>
            </div>
            <div>
              <span className="o-field-lbl">Attempts</span>
              <p className="o-num" style={{ margin: '4px 0 0' }}>{selectedFailure.attempts}</p>
            </div>
            <div>
              <span className="o-field-lbl">What the platform said</span>
              {/* Verbatim. These messages are written for the person who has to act on them —
                  "the connection needs reconnecting", "this ad account is not visible" — and
                  summarising them throws away the only part that says what to do. */}
              <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{selectedFailure.last_error}</p>
            </div>
          </div>
          <div className="o-modal-foot">
            <button type="button" className="o-btn" onClick={() => setSelectedFailure(null)}>Close</button>
            {onRetry && <button type="button" className="o-btn o-btn-primary" onClick={retrySelectedFailure}>Retry this range</button>}
          </div>
        </form>
      </div>}
    </div>
  );
}


export function DataStreamsTab({ streams, calculatedFields, onSaveCalculatedField, onUpdateStream, onRunStream, onDeleteStream, onDuplicateStream, onGoToExtractor, onSavePivot, onPivotSaved, streamsState = 'ready', streamsError = '', onRetryLoad }) {
  const [search, setSearch]         = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [columnWidths, setColumnWidths] = useState({ id: 90, name: 220, source: 190, runStatus: 150, lastRunDate: 210, lastDataDate: 180, enabled: 110, nextRunDate: 220 });
  const [selectedIds, setSelectedIds] = useState([]);
  const [view, setView]             = useState('list'); // 'list' | 'edit' | 'process' | 'history' | 'pivot'
  const [actionError, setActionError] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [detailStream, setDetailStream] = useState(null);
  const selectionAnchorRef = useRef(null);

  const filtered = streams.filter(s => {
    const platformLabel = PLATFORMS.find(p => p.id === s.platform)?.label || s.platform;
    const cadenceLabel = CADENCES_SCHEDULE.find(c => c.id === s.cadence)?.label || s.cadence;
    const values = {
      id: s.id,
      name: s.name,
      source: `${platformLabel}-Ads ${cadenceLabel}`,
      runStatus: s.runStatus,
      lastRunDate: formatRunTimestamp(s.lastRunDate),
      lastDataDate: s.lastDataDate || '',
      enabled: s.enabled ? 'Enabled' : 'Disabled',
      nextRunDate: nextRunLabel(s),
    };
    return s.name.toLowerCase().includes(search.toLowerCase())
      && Object.entries(columnFilters).every(([key, filter]) => !filter || String(values[key] ?? '').toLowerCase().includes(String(filter).toLowerCase()));
  });

  // The toolbar's action wrapper: sequential over the selection so one failure stops the
  // batch with a message naming what failed, leaving the earlier operations done.
  const runToolbarAction = async (fn) => {
    setActionBusy(true);
    setActionError('');
    try {
      await fn();
    } catch (error) {
      setActionError(error?.message || 'The action could not be completed.');
    } finally {
      setActionBusy(false);
    }
  };

  const deleteSelected = () => runToolbarAction(async () => {
    for (const id of selectedIds) await onDeleteStream(id);
    setSelectedIds([]);
    setConfirmingDelete(false);
  });

  const duplicateSelected = () => runToolbarAction(async () => {
    const source = streams.find(item => selectedIds.includes(item.id));
    if (source) await onDuplicateStream(source);
  });
  const setColumnFilter = key => value => setColumnFilters(current => ({ ...current, [key]: value }));
  const resizeColumn = key => width => setColumnWidths(current => ({ ...current, [key]: width }));
  const selectedStreams = streams.filter(s => selectedIds.includes(s.id));

  const toggleOne = (id, event = {}) => {
    const additive = Boolean(event.ctrlKey || event.metaKey);
    const range = Boolean(event.shiftKey && selectionAnchorRef.current);

    if (range) {
      const anchorIndex = filtered.findIndex(stream => stream.id === selectionAnchorRef.current);
      const clickedIndex = filtered.findIndex(stream => stream.id === id);
      if (anchorIndex >= 0 && clickedIndex >= 0) {
        const from = Math.min(anchorIndex, clickedIndex);
        const to = Math.max(anchorIndex, clickedIndex);
        const rangeIds = filtered.slice(from, to + 1).map(stream => stream.id);
        setSelectedIds(previous => additive ? [...new Set([...previous, ...rangeIds])] : rangeIds);
        return;
      }
    }

    selectionAnchorRef.current = id;
    if (additive) {
      setSelectedIds(previous => previous.includes(id)
        ? previous.filter(selectedId => selectedId !== id)
        : [...previous, id]);
      return;
    }
    setSelectedIds(previous => previous.length === 1 && previous[0] === id ? [] : [id]);
  };

  const toggleAll = () => {
    const allSelected = filtered.length > 0 && filtered.every(s => selectedIds.includes(s.id));
    setSelectedIds(allSelected ? [] : filtered.map(s => s.id));
  };

  const clearSelection = () => {
    selectionAnchorRef.current = null;
    setSelectedIds([]);
  };

  const backToList = () => setView('list');

  const th = { textAlign: 'left', padding: '10px 14px', fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${T.border}` };
  const td = { padding: '12px 14px', fontSize: 13, color: T.text, borderBottom: `1px solid ${T.border}` };

  if (view === 'edit' && selectedStreams.length === 1) {
    return (
      <div>
        <BackButton onClick={backToList} />
        <EditStreamView
          stream={selectedStreams[0]}
          onCancel={backToList}
          onSave={async draft => { await onUpdateStream(draft.id, draft); backToList(); }}
        />
      </div>
    );
  }

  if (view === 'history' && selectedStreams.length === 1) {
    return (
      <div>
        <BackButton onClick={backToList} />
        {/* Retry queues a real extraction over the daily-refresh window. It used to set
            runStatus locally, which is now server-derived and would have been a silent no-op. */}
        <ExecutionHistoryView stream={selectedStreams[0]} onRetry={run => onRunStream(selectedStreams[0].id, { from: run.date_from, to: run.date_to })} />
      </div>
    );
  }

  if (view === 'ownership' && selectedStreams.length === 1) {
    return (
      <div>
        <BackButton onClick={backToList} />
        <StreamGovernanceView stream={selectedStreams[0]} onSave={(changes) => onUpdateStream(selectedStreams[0].id, { ...selectedStreams[0], ...changes })} />
      </div>
    );
  }

  if (view === 'pivot' && selectedStreams.length === 1) {
    return (
      <div>
        <BackButton onClick={backToList} />
        <PivotTableView stream={selectedStreams[0]} streams={streams} calculatedFields={calculatedFields} onSaveCalculatedField={onSaveCalculatedField} onSavePivot={onSavePivot} onSaved={onPivotSaved} />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.text }}>
          Data Streams ({streams.length})
        </h2>
        <button type="button" className="o-btn o-btn-primary" onClick={onGoToExtractor}>Create stream</button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="search"
          className="o-control"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search data streams…"
          aria-label="Search data streams"
          style={{ width: '100%', maxWidth: 320 }}
        />

        {selectedIds.length > 0 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 12, color: T.muted, background: T.card, border: `1px solid ${T.border}`,
            borderRadius: 8, padding: '4px 10px 4px 12px',
          }}>
            {selectedIds.length} Selected
            <button type="button" onClick={clearSelection} style={{
              background: 'none', border: 'none', color: T.muted, cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0,
            }}>
              ×
            </button>
          </span>
        )}

        {selectedIds.length === 0 ? <span className="o-note" style={{ marginLeft: 'auto' }}>Select a stream to manage it</span> : <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
          <ToolbarButton label="Edit" disabled={selectedIds.length !== 1} onClick={() => setView('edit')} />
          <ToolbarButton label="Details" disabled={selectedIds.length !== 1} onClick={() => setDetailStream(streams.find(s => selectedIds.includes(s.id)) || null)} />
          <ToolbarButton label="Process" disabled={selectedIds.length === 0} onClick={() => setView('process')} />
          <ToolbarButton label="Execution History" disabled={selectedIds.length !== 1} onClick={() => setView('history')} />
          <ToolbarButton label="Ownership & audit" disabled={selectedIds.length !== 1} onClick={() => setView('ownership')} />
          <ToolbarButton label="Pivot Table" disabled={selectedIds.length !== 1} onClick={() => setView('pivot')} />
          <ToolbarButton label="Duplicate" disabled={selectedIds.length !== 1 || actionBusy} onClick={duplicateSelected} />
          <ToolbarButton label="Delete" disabled={selectedIds.length === 0 || actionBusy} onClick={() => setConfirmingDelete(true)} />
        </div>}
      </div>

      {actionError && <div className="o-state o-error" style={{ marginBottom: 16 }}>{actionError}</div>}

      {streamsState === 'error' ? (
        <div className="o-state o-error">
          <b>Could not load data streams.</b> {streamsError}
          {onRetryLoad && <div style={{ marginTop: 12 }}><button type="button" className="o-btn o-btn-sm" onClick={onRetryLoad}>Try again</button></div>}
        </div>
      ) : streamsState === 'loading' ? (
        <LoadingState title="Loading data streams…" detail="Reading stream status, schedules and latest run information." style={{ maxWidth: 'none' }} />
      ) : filtered.length === 0 ? (
        <div style={{ padding: '48px 0', textAlign: 'center', color: T.muted, fontSize: 13 }}>
          {streams.length === 0
            ? "No data streams yet — save one from the Reach extractor's Scheduler step."
            : 'No data streams match your search.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: `1px solid ${T.border}`, borderRadius: 12 }}>
          <table style={{ width: '100%', minWidth: 40 + Object.values(columnWidths).reduce((sum, width) => sum + width, 0), borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup><col style={{ width: 40 }} />{Object.values(columnWidths).map((width, index) => <col key={index} style={{ width }} />)}</colgroup>
            <thead>
              <tr>
                <th style={{ ...th, width: 36 }}>
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every(s => selectedIds.includes(s.id))}
                    onChange={toggleAll}
                  />
                </th>
                <ResizableHeaderCell width={columnWidths.id} onWidthChange={resizeColumn('id')} style={th}><ColumnHeaderFilter label="ID" value={columnFilters.id} onChange={setColumnFilter('id')} /></ResizableHeaderCell>
                <ResizableHeaderCell width={columnWidths.name} onWidthChange={resizeColumn('name')} style={th}><ColumnHeaderFilter label="Name" value={columnFilters.name} onChange={setColumnFilter('name')} /></ResizableHeaderCell>
                <ResizableHeaderCell width={columnWidths.source} onWidthChange={resizeColumn('source')} style={th}><ColumnHeaderFilter label="Source" value={columnFilters.source} onChange={setColumnFilter('source')} /></ResizableHeaderCell>
                <ResizableHeaderCell width={columnWidths.runStatus} onWidthChange={resizeColumn('runStatus')} style={th}><ColumnHeaderFilter label="Run Status" value={columnFilters.runStatus} onChange={setColumnFilter('runStatus')} options={[...new Set(streams.map(item => item.runStatus))]} /></ResizableHeaderCell>
                <ResizableHeaderCell width={columnWidths.lastRunDate} onWidthChange={resizeColumn('lastRunDate')} style={th}><ColumnHeaderFilter label="Last Run Date" value={columnFilters.lastRunDate} onChange={setColumnFilter('lastRunDate')} /></ResizableHeaderCell>
                <ResizableHeaderCell width={columnWidths.lastDataDate} onWidthChange={resizeColumn('lastDataDate')} style={th}><ColumnHeaderFilter label="Last Data Date" value={columnFilters.lastDataDate} onChange={setColumnFilter('lastDataDate')} /></ResizableHeaderCell>
                <ResizableHeaderCell width={columnWidths.enabled} onWidthChange={resizeColumn('enabled')} style={th}><ColumnHeaderFilter label="Status" value={columnFilters.enabled} onChange={setColumnFilter('enabled')} options={['Enabled', 'Disabled']} /></ResizableHeaderCell>
                <ResizableHeaderCell width={columnWidths.nextRunDate} onWidthChange={resizeColumn('nextRunDate')} style={th}><ColumnHeaderFilter label="Next Run Date" value={columnFilters.nextRunDate} onChange={setColumnFilter('nextRunDate')} /></ResizableHeaderCell>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => {
                const platformLabel = PLATFORMS.find(p => p.id === s.platform)?.label || s.platform;
                const cadenceLabel  = CADENCES_SCHEDULE.find(c => c.id === s.cadence)?.label || s.cadence;
                const selected = selectedIds.includes(s.id);
                return (
                  <tr
                    key={s.id}
                    onClick={event => toggleOne(s.id, event)}
                    style={{ cursor: 'pointer', background: selected ? T.accentBg : 'transparent' }}
                  >
                    <td style={td} onClick={event => event.stopPropagation()}>
                      <input type="checkbox" checked={selected} onClick={event => event.stopPropagation()} onChange={event => toggleOne(s.id, event.nativeEvent)} />
                    </td>
                    {/* Six characters of the UUID, on one line. The full id wraps over four
                        lines at this width and pushes every other column sideways, for a value
                        nobody reads in full — it is used to eyeball "same row?" and to copy.
                        `title` keeps the whole thing available on hover and to select. */}
                    <td style={{ ...td, color: T.muted, whiteSpace: 'nowrap' }} title={s.id}>
                      <span className="o-mono">{shortStreamId(s.id)}</span>
                    </td>
                    <td
                      style={{ ...td, cursor: 'text' }}
                      title="Double-click to edit this stream"
                      onDoubleClick={event => {
                        event.stopPropagation();
                        setSelectedIds([s.id]);
                        setView('edit');
                      }}
                    >
                      {s.name}
                    </td>
                    <td style={{ ...td, color: T.muted }}>{platformLabel}-Ads {cadenceLabel}</td>
                    <td style={td}><RunStatusBadge status={s.runStatus} /></td>
                    {/* Same format as the history screen — the raw ISO timestamp and the
                        formatted one were a screen apart, for the same value. */}
                    <td style={{ ...td, color: T.muted }}>{formatRunTimestamp(s.lastRunDate) || '—'}</td>
                    <td style={{ ...td, color: T.muted }}>{s.lastDataDate || ''}</td>
                    <td style={td}>
                      <StatusToggle on={s.enabled} disabled={actionBusy} onChange={val => runToolbarAction(() => onUpdateStream(s.id, { ...s, enabled: val }))} />
                    </td>
                    {/* The scheduler's own timestamp. A skipped stream reads in the warning
                        tone with its reason on hover and in full in the Details panel. */}
                    <td style={{ ...td, color: s.enabled && s.scheduleError ? O.warn : T.muted }} title={s.scheduleError || undefined}>{nextRunLabel(s)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {view === 'process' && selectedStreams.length > 0 && (
        <Modal onClose={backToList}>
          <ProcessView streams={selectedStreams} onRun={onRunStream} />
        </Modal>
      )}

      {confirmingDelete && (
        <div className="o-scrim" role="dialog" aria-modal="true" onClick={() => setConfirmingDelete(false)}>
          <div className="o-modal" onClick={event => event.stopPropagation()} style={{ width: 'min(440px, 94vw)' }}>
            <div className="o-modal-head"><h2 style={{ margin: 0, fontSize: 18 }}>Delete {selectedIds.length === 1 ? 'this stream' : `${selectedIds.length} streams`}?</h2></div>
            <div className="o-modal-body">
              <p style={{ margin: 0 }}>
                Configuration and queued runs are removed; extracted rows and run history are
                kept. This cannot be undone.
              </p>
              <ul style={{ margin: '12px 0 0', paddingLeft: 20 }}>
                {streams.filter(s => selectedIds.includes(s.id)).map(s => <li key={s.id}>{s.name}</li>)}
              </ul>
            </div>
            <div className="o-modal-foot">
              <button type="button" className="o-btn" onClick={() => setConfirmingDelete(false)}>Cancel</button>
              <button type="button" className="o-btn o-btn-danger" disabled={actionBusy} onClick={deleteSelected}>{actionBusy ? 'Deleting…' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}

      {detailStream && <StreamDetailModal stream={detailStream} onClose={() => setDetailStream(null)} />}
    </div>
  );
}


/**
 * Everything about one stream in one place. Before this, answering "what does this stream
 * extract, with which email, on what schedule, and why is it not running" took four screens
 * and a hover title.
 */
export function StreamDetailModal({ stream, onClose }) {
  const row = (label, value) => (
    <div style={{ display: 'grid', gridTemplateColumns: '160px minmax(0, 1fr)', gap: 12 }}>
      <span className="o-field-lbl" style={{ alignSelf: 'baseline' }}>{label}</span>
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{value}</span>
    </div>
  );
  const scheduleText = stream.cadence === 'weekly'
    ? `Weekly on ${(stream.weekdays || []).join(', ') || 'Monday'} at ${stream.time}`
    : stream.cadence === 'monthly'
      ? `Monthly on the ${ordinal(stream.monthDay || 1)} at ${stream.time}`
      : `Daily at ${stream.time}`;

  return <div className="o-scrim" role="dialog" aria-modal="true" onClick={onClose}>
    <div className="o-modal" onClick={event => event.stopPropagation()} style={{ width: 'min(560px, 94vw)' }}>
      <div className="o-modal-head">
        <h2 style={{ margin: 0, fontSize: 18 }}>{stream.name}</h2>
        <button type="button" className="o-btn o-btn-ghost o-btn-sm" aria-label="Close stream details" onClick={onClose}>×</button>
      </div>
      <div className="o-modal-body" style={{ display: 'grid', gap: 12 }}>
        {row('Stream id', <span className="o-code">{stream.id}</span>)}
        {row('Platform', PLATFORMS.find(item => item.id === stream.platform)?.label || stream.platform)}
        {row('Connection', stream.connectionLabel
          ? <>{stream.connectionLabel}{stream.connectionStatus && stream.connectionStatus !== 'active' && <span className="o-badge o-err" style={{ marginLeft: 8 }}>{stream.connectionStatus}</span>}</>
          : 'None — this stream cannot extract until one is chosen')}
        {row('Ad account', <span className="o-code">{stream.adAccounts?.[0] || 'not set'}</span>)}
        {row('Dimensions', (stream.levels || []).map(labelForLevel).join(', ') || 'All supported dimensions')}
        {row('Metrics', (stream.kpis || []).map(labelForKpi).join(', ') || 'All supported metrics')}
        {row('Schedule', `${scheduleText} · ${stream.timezone || 'UTC'}`)}
        {row('Status', stream.enabled ? 'Enabled' : 'Paused')}
        {row('Last run', formatRunTimestamp(stream.lastRunDate) || 'Never')}
        {row('Last data', stream.lastDataDate || '—')}
        {row('Next run', nextRunLabel(stream))}
        {stream.scheduleError && row('Not running because', stream.scheduleError)}
        {stream.lastError && row('Last failure', <span style={{ whiteSpace: 'pre-wrap' }}>{stream.lastError}</span>)}
        {stream.owner && row('Owner', stream.owner)}
        {(stream.tags || []).length > 0 && row('Tags', stream.tags.join(', '))}
      </div>
      <div className="o-modal-foot">
        <button type="button" className="o-btn" onClick={onClose}>Close</button>
      </div>
    </div>
  </div>;
}


export function StreamGovernanceView({ stream, onSave }) {
  const [owner, setOwner] = useState(stream.owner || stream.email || 'ops@agency.com');
  const [tags, setTags] = useState((stream.tags || [stream.platform]).join(', '));
  const [saved, setSaved] = useState(false);
  const events = [
    { id: 'updated', at: stream.lastRunDate || 'Not run yet', actor: owner, action: 'Stream configuration updated', detail: `${stream.cadence} at ${stream.time}` },
    { id: 'accounts', at: stream.lastRunDate || 'Not run yet', actor: owner, action: 'Source accounts verified', detail: stream.adAccounts.join(', ') },
    { id: 'status', at: stream.lastRunDate || 'Not run yet', actor: 'Reach scheduler', action: `Run marked ${stream.runStatus.toLowerCase()}`, detail: stream.lastDataDate || 'No data watermark recorded' },
  ];
  const save = () => { onSave({ owner: owner.trim(), tags: tags.split(',').map(tag => tag.trim()).filter(Boolean) }); setSaved(true); };
  return <div><div className="o-head-row"><div><h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Ownership &amp; audit — {stream.name}</h2><p className="o-note" style={{ margin: 0 }}>Manage responsibility and review activity for this data stream.</p></div></div><section className="o-panel" style={{ padding: 20, marginBottom: 16 }}><div className="reach-form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}><label><span className="o-field-lbl">Stream owner</span><input className="o-control" type="email" value={owner} onChange={event => { setOwner(event.target.value); setSaved(false); }} style={{ marginTop: 8 }} /></label><label><span className="o-field-lbl">Tags</span><input className="o-control" value={tags} onChange={event => { setTags(event.target.value); setSaved(false); }} placeholder="paid-social, weekly" style={{ marginTop: 8 }} /></label></div><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><button type="button" className="o-btn o-btn-primary" disabled={!owner.trim()} onClick={save}>Save ownership</button>{saved && <span className="o-note">Ownership saved.</span>}</div></section><div className="o-panel" style={{ overflowX: 'auto' }}><table className="o-tbl" style={{ width: '100%', minWidth: 780 }}><thead><tr><th>Time</th><th>Actor</th><th>Action</th><th>Detail</th></tr></thead><tbody>{events.map(item => <tr key={item.id}><td style={{ whiteSpace: 'nowrap' }}>{item.at}</td><td>{item.actor}</td><td className="o-strong">{item.action}</td><td>{item.detail}</td></tr>)}</tbody></table></div></div>;
}

